import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import type { ActionPerformed, PushNotificationSchema } from '@capacitor/push-notifications';
import { useAuthState } from '@/hooks/useAuth';
import { NOTIFICATION_TRACKING_ENDPOINT } from '@/config/endpoints';

/**
 * Hook to handle iOS Universal Links, deep links, and push notification taps
 * Handles both cold start (app was closed) and warm start (app in background)
 * 
 * NOTE: Universal Links ONLY work when:
 * - Tapping a link from another app (Messages, Mail, Notes, etc.)
 * - Tapping a link from a different website
 * - Long-pressing a link and selecting "Open in App"
 * 
 * Universal Links DO NOT work when:
 * - Typing URL directly in Safari address bar
 * - Already browsing the same website
 */
export const useDeepLink = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuthState();
  const userRef = useRef(user);
  const pendingDeepLinkRef = useRef<string | null>(null);
  
  userRef.current = user;

  // Track notification events
  const trackNotificationEvent = async (data: any, event: 'clicked' | 'dismissed') => {
    try {
      const notificationId = data?.notificationId;
      const chatId = data?.chatId;
      
      if (!notificationId) {
        console.warn('[DeepLink] ⚠️ Cannot track notification event - no notificationId found');
        return;
      }
      
      const payload = {
        notificationId,
        event,
        timestamp: new Date().toISOString(),
        ...(chatId && { chatId }),
        ...(userRef.current?.uid && { userId: userRef.current.uid })
      };
      
      console.log(`[DeepLink] 📊 Tracking notification ${event}:`, payload);
      
      const response = await fetch(NOTIFICATION_TRACKING_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      
      if (response.ok) {
        console.log(`[DeepLink] ✅ Notification ${event} tracked successfully`);
      } else {
        console.warn(`[DeepLink] ⚠️ Notification tracking returned status ${response.status}`);
      }
    } catch (error) {
      console.error(`[DeepLink] ❌ Failed to track notification ${event}:`, error);
    }
  };

  // Parse a URL to extract the navigation path
  const parseDeepLinkUrl = (url: string): string => {
    if (url.startsWith('/') || url.startsWith('?')) {
      return url;
    }
    
    try {
      const urlObj = new URL(url);
      return urlObj.pathname + urlObj.search + urlObj.hash;
    } catch {
      console.warn('[DeepLink] Could not parse as absolute URL, treating as path:', url);
      return url.startsWith('/') ? url : `/${url}`;
    }
  };

  // Navigate to a deep link path
  const navigateToDeepLink = (url: string, source: string) => {
    if (authLoading) {
      console.log('[DeepLink] 🔄 Auth still loading, storing pending deep link:', url);
      pendingDeepLinkRef.current = url;
      return;
    }

    const path = parseDeepLinkUrl(url);
    console.log(`[DeepLink] 🔗 Processing ${source} → ${path}`);
    
    navigate(path, { replace: true });
    console.log('[DeepLink] ✅ Navigation complete to:', path);
  };

  // Navigate from a notification tap
  const navigateFromNotification = (url: string) => {
    navigateToDeepLink(url, 'notification tap');
  };

  // Process notification taps
  const handleNotificationTap = (action: ActionPerformed) => {
    const data = action.notification.data;
    
    console.log('[DeepLink] 🔔 Notification tapped');
    console.log('[DeepLink] Data:', JSON.stringify(data, null, 2));
    
    trackNotificationEvent(data, 'clicked');
    
    if (!data) {
      console.warn('[DeepLink] ❌ No data in notification');
      return;
    }
    
    // Check for URL in various fields (FCM/APNS payloads)
    const url = data.url || data.link || data.fcm_options?.link;
    
    if (url) {
      console.log('[DeepLink] ✅ Found URL in notification data');
      navigateFromNotification(url);
      return;
    }
    
    // Fallback: Check for specific data fields like chatId
    if (data.chatId) {
      console.log('[DeepLink] ✅ Found chatId in notification data');
      navigateFromNotification(`/chats/${data.chatId}`);
      return;
    }
    
    console.warn('[DeepLink] ❌ No deep link URL found in notification');
  };

  // Set up native platform listeners
  const setupNativeListeners = async () => {
    const isNative = Capacitor.isNativePlatform();
    
    if (!isNative) {
      console.log('[DeepLink] ⚠️ Not on native platform - skipping native listeners');
      return;
    }

    console.log('[DeepLink] 📱 Setting up native listeners');
    
    let appUrlListenerHandle: any;
    let notificationTapListenerHandle: any;
    let notificationReceivedListenerHandle: any;

    try {
      // Check for cold start URL
      const launchUrl = await App.getLaunchUrl();
      if (launchUrl?.url) {
        console.log('[DeepLink] 🚀 Cold start with URL:', launchUrl.url);
        navigateToDeepLink(launchUrl.url, 'cold start');
      }

      // Listen for warm start (app in background)
      appUrlListenerHandle = await App.addListener('appUrlOpen', (data: any) => {
        console.log('[DeepLink] 🔥 Warm start with URL:', data.url);
        navigateToDeepLink(data.url, 'warm start');
      });

      // Listen for notification taps
      notificationTapListenerHandle = await PushNotifications.addListener(
        'pushNotificationActionPerformed',
        handleNotificationTap
      );

      // Listen for foreground notifications (for debugging)
      notificationReceivedListenerHandle = await PushNotifications.addListener(
        'pushNotificationReceived',
        (notification: PushNotificationSchema) => {
          console.log('[DeepLink] 📬 Notification received (foreground):', notification.title);
        }
      );

      console.log('[DeepLink] ✅ Native listeners registered');

      return () => {
        appUrlListenerHandle?.remove();
        notificationTapListenerHandle?.remove();
        notificationReceivedListenerHandle?.remove();
      };
    } catch (error) {
      console.error('[DeepLink] ❌ Error setting up native listeners:', error);
    }
  };

  // Initialize hook
  useEffect(() => {
    console.log('[DeepLink] 🚀 Initializing deep link hook');
    console.log('[DeepLink] Platform:', Capacitor.getPlatform());
    console.log('[DeepLink] Native:', Capacitor.isNativePlatform());
  }, []);

  // Set up listeners once
  useEffect(() => {
    const cleanup = setupNativeListeners();
    return () => {
      cleanup?.then(fn => fn?.());
    };
  }, []);

  // Process pending deep link once auth is ready
  useEffect(() => {
    if (authLoading || !pendingDeepLinkRef.current) {
      return;
    }

    const pending = pendingDeepLinkRef.current;
    pendingDeepLinkRef.current = null;
    
    console.log('[DeepLink] ✅ Auth ready, processing pending deep link:', pending);
    navigateToDeepLink(pending, 'pending (auth ready)');
  }, [authLoading]);
};
